import { ProTable, type ProColumns } from '@ant-design/pro-components'
import { Alert, Button, DatePicker, Empty, Select } from 'antd'
import dayjs from 'dayjs'
import type { CustomerIssuePreview } from '@super-mega/server/contracts/bill'
import { formatShanghaiDateTime } from '@/const/date.ts'
import { RANGE_PRESETS } from '@/const/range-presets.ts'
import { BILL_KINDS } from '@/features/bill/index.ts'
import { getCreatedAtSearch } from '@/utils/list-query.ts'
import { formatAmount } from '@/utils/money.ts'
import { getPreviousMonthCreatedTime } from './search.ts'

type Row = CustomerIssuePreview['data'][number]
type Range = ReturnType<typeof getPreviousMonthCreatedTime>
const columns: ProColumns<Row>[] = [
  { title: 'ID', dataIndex: 'id', copyable: true, width: '5rem' },
  { title: '账单号', dataIndex: 'billNo', copyable: true, width: '13rem' },
  { title: '经营主体', dataIndex: 'organizationName', ellipsis: true, width: '12rem' },
  { title: '费用类型', dataIndex: 'feeType', valueEnum: BILL_KINDS, width: '7rem' },
  { title: '状态', dataIndex: 'status', valueEnum: { draft: { text: '草稿', status: 'Default' } }, width: '6rem' },
  {
    title: '账单金额',
    dataIndex: 'originalAmount',
    align: 'right',
    width: '8rem',
    renderText: (value: string) => `¥${formatAmount(value)}`
  },
  {
    title: '创建时间',
    dataIndex: 'createdAt',
    width: '11rem',
    renderText: (value: Date) => formatShanghaiDateTime(value)
  }
]

export function CustomerIssuePreviewContent({
  data,
  range,
  loading,
  error,
  accounts,
  pagination,
  onRangeChange,
  onPageChange,
  onAccountChange,
  onRefresh
}: {
  data?: CustomerIssuePreview
  range: Range
  loading: boolean
  error?: string
  accounts: Record<number, number | undefined>
  pagination: { current: number; pageSize: number }
  onRangeChange: (range: Range) => void
  onPageChange: (current: number, pageSize: number) => void
  onAccountChange: (organizationId: number, accountId: number) => void
  onRefresh: () => void
}) {
  let emptyDescription = '当前时间范围内没有可出账账单'
  if (loading || !data) emptyDescription = '正在加载账单…'
  if (error) emptyDescription = '账单预览加载失败'

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <label htmlFor="customer-issue-created-from" className="font-medium">
          创建时间
        </label>
        <DatePicker.RangePicker
          presets={RANGE_PRESETS}
          id={{ start: 'customer-issue-created-from', end: 'customer-issue-created-to' }}
          className="w-full sm:w-72"
          value={[dayjs(range.createdFrom).utcOffset(8), dayjs(range.createdTo).utcOffset(8)]}
          allowClear={false}
          onChange={(dates) => {
            const next = getCreatedAtSearch(dates)
            if (next.createdFrom && next.createdTo)
              onRangeChange({ createdFrom: next.createdFrom, createdTo: next.createdTo })
          }}
        />
        <Button onClick={() => onRangeChange(getPreviousMonthCreatedTime())}>上个自然月</Button>
      </div>
      <p className="text-content-muted m-0 text-sm">
        仅预览当前客户在所选创建时间内未归组、未归档且金额大于零的草稿账单，确认后统一出账并生成收款通知单。
      </p>
      {error && <Alert type="error" showIcon title={error} action={<Button onClick={onRefresh}>重新预览</Button>} />}
      {data && !error && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <span className="font-medium">本次可出账 {data.billCount} 张账单</span>
          {(data.pendingCount > 0 || data.zeroCount > 0) && (
            <span className="text-content-muted">
              跳过待核金额 {data.pendingCount} 张、零金额 {data.zeroCount} 张。
            </span>
          )}
        </div>
      )}
      {data && !loading && !error && data.total === 0 ? (
        <div className="py-6">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyDescription} />
        </div>
      ) : (
        <ProTable<Row>
          rowKey="id"
          size="small"
          columns={columns}
          columnsState={{ persistenceKey: 'customer-issue-preview-columns', persistenceType: 'localStorage' }}
          dataSource={error ? [] : (data?.data ?? [])}
          loading={loading}
          search={false}
          cardProps={false}
          toolBarRender={false}
          options={false}
          scroll={data?.total ? { x: '60rem' } : undefined}
          locale={{
            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyDescription} />
          }}
          pagination={{
            ...pagination,
            total: data?.total ?? 0,
            showSizeChanger: true,
            hideOnSinglePage: true,
            showTotal: (total) => `共 ${total} 张`,
            onChange: onPageChange
          }}
        />
      )}
      {data &&
        !error &&
        data.organizations.map((org) => (
          <div key={org.id} className="space-y-2">
            <label htmlFor={`issue-account-${org.id}`}>
              {org.name} · {org.billCount} 张 · ¥{formatAmount(org.amount)} · 收款账户
            </label>
            <Select
              id={`issue-account-${org.id}`}
              className="w-full"
              value={accounts[org.id]}
              placeholder="请选择收款账户"
              options={org.accounts.map((account) => ({ value: account.id, label: account.label }))}
              onChange={(accountId: number) => onAccountChange(org.id, accountId)}
            />
            {!org.accounts.length && (
              <Alert type="warning" showIcon title="该主体没有可用收款账户，请先在经营主体中配置，再重新预览。" />
            )}
          </div>
        ))}
    </div>
  )
}
